/**
 * The question catalogue (crossmarc/steward-app#2552): one list of every question the client is
 * asked, DERIVED from the two schemas that ask them, so a future reader consults it rather than
 * growing a new per-key list. Prep for letting a firm reorganise questions into different steps
 * and for docs/40's question-keyed staff record.
 *
 *   - the SNAPSHOT step: the leaf paths of {@link IntakeQueueSubmissionSchema} (the honeypot is
 *     not on it), walked here; and
 *   - the ONBOARDING step: the keys of the `onboarding-details` row, from
 *     {@link ONBOARDING_SECTION_KEYS} and {@link ONBOARDING_SERVER_OWNED_KEYS}.
 *
 * `questionCatalogue.test.ts` pins the result one-to-one with both schemas, deriving the
 * expected set a second, independent way (zod's JSON Schema export for the snapshot; the pack
 * field list for the onboarding row), and carries a dated, literal list of ids as a tripwire: a
 * derived catalogue follows its schemas by construction, so it is that literal list, not the
 * derivation, that fails when either schema grows a field, until the catalogue is looked at. Out of scope here, deliberately: which step asks what, any firm
 * configuration, and any storage change.
 *
 * ## Ids
 *
 *   - SNAPSHOT: the dotted path, e.g. `aboutYou.firstName`, `spousePartner.spouse.firstName`.
 *     A LIST is itself a question (`cashAccounts`, kind `list`) and each field of its items is a
 *     question addressed through the list: `cashAccounts[].institution`. A multi-select of plain
 *     values (`insuranceAndEstate.estateDocuments`) is one `answer` question, not a list.
 *   - ONBOARDING: the row's own key, e.g. `client_firstName`. The two id spaces are not
 *     separated by syntax (marker keys such as `sentAt` and top-level snapshot ids such as
 *     `realEstate` both have no separator); `questionCatalogue.test.ts` asserts every id is unique.
 *
 * The id is NOT prefixed with the step, because the step is a property of the question that a
 * firm may later change; the id must survive that. The same real-world question asked in both
 * steps today (a first name) is two catalogue entries; joining them is the single client
 * profile's work, not this file's.
 *
 * ## List items are addressed by a minted item id, not by index
 *
 * An answer inside a list is addressed by {@link questionAnswerKey}: the item-field question id
 * plus the item's id. The item id has the shape of `IntakeRowIdSchema` (16 to 32 letters and
 * digits, minted by the browser: `intakeRowId.ts`), re-exported as {@link QuestionItemIdSchema}
 * so there is one shape. ⚠️ **As of 2026-10-24 stored rows do not yet carry item ids**: only the
 * snapshot's cash and investment account rows have a `rowId` (#2185), children and every other
 * list are index-aligned, and the onboarding row's lists (`children_names`,
 * `children_dateOfBirths`, `requests_answerIds`, `requests_answerTexts`) are parallel string
 * arrays with no item ids at all. This file describes the questions; it migrates no data, and
 * nothing mints or reads an item id through it yet. The `rowId` key itself is the item id, so it
 * is not a question and is absent from the catalogue.
 *
 * ## Sensitivity classes: RECORDED, NOT ENFORCED
 *
 * Each question carries a set of {@link QuestionClass}. Nothing in the codebase consults the
 * `protected-identity` or `assistant-excluded` classes as of 2026-10-24; they exist so the
 * readers that need them consult one place. What each says, and what the code does today:
 *
 *   - `firm-visible`: the key is returned by the firm's read of a client's onboarding details
 *     (`getClientOnboardingDetails`). THIS ONE IS ENFORCED: that handler's allow-list is
 *     {@link firmVisibleOnboardingKeys}, derived from here. `questionCatalogue.test.ts` compares
 *     it with the set the handler used before (rebuilt there from the same two lists, so it
 *     guards the class assignment) and pins its size and the full id list as of 2026-10-24 (`ONBOARDING_SECTION_KEYS` plus
 *     `ONBOARDING_SERVER_OWNED_KEYS`, which is 2026-10-24's set). It speaks only of that route; it
 *     says nothing about who may see a snapshot answer.
 *   - `protected-identity`: the SSN and the sealed identity record. By design these never sit on
 *     `onboarding-details` (see `onboardingForm.ts`, "No identity number here, ever"). No
 *     question in either schema holds one, so NO catalogue entry carries this class today; the
 *     test pins that, and that no onboarding key is named like an identity number. The class is
 *     here so a future question that did carry one has a place to say so, and a test that fails
 *     until someone looks.
 *   - `assistant-excluded`: the Steward assistant must not read this answer. As of a grep on
 *     2026-10-24 the assistant reads NO onboarding details (no reader of `onboarding-details`
 *     exists on its path), so this is not enforced anywhere. It is set on every onboarding-step
 *     question as a conservative default pending a decision by Matt, and on any
 *     `protected-identity` question unconditionally (no such question exists yet). Snapshot
 *     questions carry no assistant class: not decided here.
 */
import { z } from 'zod';
import { INTAKE_ROW_ID_KEY, IntakeRowIdSchema } from './intakeRowId.js';
import { IntakeQueueSubmissionSchema } from './intakeMessage.js';
import { ONBOARDING_SECTION_KEYS, ONBOARDING_SERVER_OWNED_KEYS } from './onboardingForm.js';

export type QuestionStep = 'snapshot' | 'onboarding';
export type QuestionClass = 'firm-visible' | 'protected-identity' | 'assistant-excluded';

/**
 * `answer`: one value (text, choice, yes/no, a multi-select). `list`: a repeating group, or an
 * onboarding string-list answer; a snapshot list's item fields are the `answer` entries whose
 * `listId` names it. `marker`: an onboarding row key that is server-owned rather than asked
 * (`sentAt`, `identityProvidedClient`, the per-section saved flags).
 */
export type QuestionKind = 'answer' | 'list' | 'marker';

export interface Question {
  id: string;
  step: QuestionStep;
  kind: QuestionKind;
  /** For an item field: the id of the list question it belongs to, and so what needs an item id. */
  listId?: string;
  classes: readonly QuestionClass[];
}

/** The shape of an item id. The same as a snapshot row's `rowId`: one shape, not two. */
export const QuestionItemIdSchema = IntakeRowIdSchema;

/**
 * The key an answer is filed under: the question id, and for an item field also the item's id
 * (`cashAccounts[].institution#a1b2c3d4e5f6a7b8`). Throws when an item field is given no item id
 * or a non-item question is given one, since either would silently address the wrong thing.
 */
export function questionAnswerKey(question: Question, itemId?: string): string {
  if (question.listId === undefined) {
    if (itemId !== undefined) throw new Error(`question ${question.id} is not a list item field`);
    return question.id;
  }
  if (itemId === undefined || !QuestionItemIdSchema.safeParse(itemId).success) {
    throw new Error(`question ${question.id} needs a minted item id`);
  }
  return `${question.id}#${itemId}`;
}

type Def = { type: string; shape?: Record<string, z.ZodType>; element?: z.ZodType; innerType?: z.ZodType };
const defOf = (s: z.ZodType): Def => (s as unknown as { _zod: { def: Def } })._zod.def;

/** Strip `optional`/`nullable`/`default` wrappers. */
function unwrap(s: z.ZodType): z.ZodType {
  let cur = s;
  for (;;) {
    const d = defOf(cur);
    if ((d.type === 'optional' || d.type === 'nullable' || d.type === 'default') && d.innerType) cur = d.innerType;
    else return cur;
  }
}

const LEAF_TYPES: ReadonlySet<string> = new Set(['string', 'number', 'boolean', 'enum', 'literal', 'array']);

function walkSnapshot(schema: z.ZodType, path: string, listId: string | undefined, out: Question[]): void {
  const d = defOf(unwrap(schema));
  if (d.type !== 'object' || !d.shape) throw new Error(`catalogue walk: ${path} is not an object`);
  for (const [key, child] of Object.entries(d.shape)) {
    if (listId !== undefined && key === INTAKE_ROW_ID_KEY) continue; // the item id, not a question
    const id = path === '' ? key : `${path}.${key}`;
    const inner = unwrap(child);
    const cd = defOf(inner);
    if (cd.type === 'object') {
      walkSnapshot(inner, id, listId, out);
    } else if (cd.type === 'array' && cd.element && defOf(unwrap(cd.element)).type === 'object') {
      out.push({ id, step: 'snapshot', kind: 'list', ...(listId ? { listId } : {}), classes: [] });
      walkSnapshot(cd.element, `${id}[]`, id, out);
    } else {
      if (!LEAF_TYPES.has(cd.type)) {
        // Unions, pipes, records, lazies and the like may hide an object; refuse rather than guess.
        throw new Error(`catalogue walk: ${id} has unhandled schema type '${cd.type}'`);
      }
      out.push({ id, step: 'snapshot', kind: 'answer', ...(listId ? { listId } : {}), classes: [] });
    }
  }
}

const ONBOARDING_LISTS: ReadonlySet<string> = new Set([
  'children_names', 'children_dateOfBirths', 'requests_answerIds', 'requests_answerTexts',
]);

function buildCatalogue(): readonly Question[] {
  const out: Question[] = [];
  walkSnapshot(IntakeQueueSubmissionSchema, '', undefined, out);
  const firm: QuestionClass[] = ['firm-visible', 'assistant-excluded'];
  for (const key of ONBOARDING_SECTION_KEYS) {
    out.push({ id: key, step: 'onboarding', kind: ONBOARDING_LISTS.has(key) ? 'list' : 'answer', classes: firm });
  }
  for (const key of ONBOARDING_SERVER_OWNED_KEYS) {
    out.push({ id: key, step: 'onboarding', kind: 'marker', classes: firm });
  }
  return out;
}

/** Every question, snapshot step first, in schema order. Built once at import. */
export const QUESTION_CATALOGUE: readonly Question[] = buildCatalogue();

const BY_ID: ReadonlyMap<string, Question> = new Map(QUESTION_CATALOGUE.map((q) => [q.id, q] as const));

export function questionById(id: string): Question | undefined {
  return BY_ID.get(id);
}

export function questionsForStep(step: QuestionStep): Question[] {
  return QUESTION_CATALOGUE.filter((q) => q.step === step);
}

export function questionHasClass(q: Question, c: QuestionClass): boolean {
  return q.classes.includes(c);
}

/**
 * The `onboarding-details` keys the firm's read may return: the `firm-visible` onboarding
 * questions. The façade's allow-list (`getClientOnboardingDetails`) is built from this.
 */
export function firmVisibleOnboardingKeys(): ReadonlySet<string> {
  return new Set(
    QUESTION_CATALOGUE.filter((q) => q.step === 'onboarding' && questionHasClass(q, 'firm-visible')).map((q) => q.id),
  );
}
